import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import api from "service/api";
import Select from "react-select";
import debounce from "lodash.debounce";
import qs from "query-string";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Container,
  Row,
  Col,
  Tooltip,
} from "reactstrap";
import { ReactComponent as XIcon } from "assets/icons/x_icon.svg";

const getParamsId = () => {
  const { id } = qs.parse(window.location.search);
  return id || undefined;
};

const LibraryForm = () => {
  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [term, setTerm] = useState("");
  const [terms, setTerms] = useState([]);
  const [references, setReferences] = useState([]);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [libraryId, setLibraryId] = useState(() => getParamsId());

  useEffect(() => {
    getClients();
    if (libraryId) {
      fetchLibraryById(libraryId);
    }
  }, []);

  const getClients = async () => {
    try {
      const response = await api.get("/clients");
      const clientOptions = response.data.clients.map((client) => ({
        value: client.id,
        label: client.name,
      }));
      setClients(clientOptions);
      const clientId = getParamsClientId();
      if (clientId) {
        const client = clientOptions.find((c) => c.value === clientId);
        if (client) {
          setSelectedClient(client);
        }
      }
    } catch (error) {
      toast.error("Erro ao recuperar clientes!");
    }
  };

  const getParamsClientId = () => {
    const queryParams = qs.parse(window.location.search);
    const id = queryParams.clientId;
    return id ? id : undefined;
  };

  const fetchLibraries = async (query, client) => {
    setLoadingOptions(true);
    try {
      const clientId = client && client.value ? client.value : "";
      const response = await api.get(
        `/libraries/autocomplete?search=${query}&client_id=${clientId}`,
      );
      const libraryOptions = response.data.map((library) => ({
        label: library.title,
        value: library.id,
      }));
      setOptions(libraryOptions);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingOptions(false);
    }
  };

  const fetchLibraryById = async (id) => {
    try {
      const { data: lib } = await api.get(`/library/${id}`);
      setTitle(lib.title);
      setDescription(lib.description);
      setSelectedClient(
        lib.client ? { value: lib.client.id, label: lib.client.name } : null,
      );
      setTerms(lib.terms.map((t) => t.term));
      setReferences(
        lib.referencedLibraries.map((r) => ({ value: r.id, label: r.title })),
      );
    } catch {
      toast.error("Erro ao carregar biblioteca");
    }
  };

  const debouncedFetchLibraries = useCallback(
    debounce((query) => fetchLibraries(query, selectedClient), 300),
    [selectedClient],
  );

  useEffect(() => {
    return () => {
      debouncedFetchLibraries.cancel();
    };
  }, [debouncedFetchLibraries]);

  const resetFormState = () => {
    setLoading(false);
    setTerms([]);
    setReferences([]);
    setOptions([]);
    setDescription("");
    setTitle("");
    reset();
  };

  const onSubmit = async (data) => {
    if (terms.length === 0) {
      toast.error("É obrigatório adicionar pelo menos um termo");
      return;
    }
    setLoading(true);

    data.clientId = selectedClient ? selectedClient.value : null;
    data.terms = terms.map((t) => ({ term: t }));
    data.referencedLibraries = references.map((r) => r.value);

    try {
      if (libraryId) {
        await api.put(`/libraries/${libraryId}`, data);
        toast.success("Biblioteca atualizada com sucesso!");
        setLibraryId(undefined);
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      } else {
        await api.post("/libraries", data);
        toast.success("Biblioteca cadastrada com sucesso!");
      }
      resetFormState();
    } catch (error) {
      setLoading(false);
      const { status, data: errData } = error.response || {};
      if (status === 400 && errData?.message === "Title already exists.") {
        toast.error("Já existe uma biblioteca com esse título");
      } else if (
        status === 400 &&
        errData?.message?.includes("referenced by another library")
      ) {
        toast.error(
          "Esta biblioteca é referência de outra e não pode conter referências.",
        );
      } else if (
        status === 400 &&
        errData?.message?.includes("already has its own references")
      ) {
        toast.error(
          "A biblioteca que você está tentando referenciar já possui suas próprias referências.",
        );
      } else if (
        status === 400 &&
        errData?.message?.includes("does not belong to the same client")
      ) {
        toast.error("A biblioteca referenciada não pertence ao mesmo cliente.");
      } else if (
        status === 400 &&
        errData?.message?.includes("does not exist")
      ) {
        toast.error("A biblioteca referenciada não foi encontrada.");
      } else {
        toast.error("Erro ao salvar biblioteca");
      }
    }
  };

  const handleAddTerm = (e) => {
    e.preventDefault();
    clearErrors("term");
    if (term.trim() && !term.startsWith("@") && !terms.includes(term.trim())) {
      setTerms([...terms, term.trim()]);
      setTerm("");
    }
  };

  const handleRemoveTerm = (termToRemove) => {
    setTerms(terms.filter((t) => t !== termToRemove));
  };

  const handleRemoveReference = (referenceId) => {
    setReferences(references.filter((ref) => ref.value !== referenceId));
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setTerm(inputValue);
    clearErrors("term");
    if (inputValue.startsWith("@") && inputValue.length > 2) {
      debouncedFetchLibraries(inputValue.slice(1));
    } else {
      setOptions([]);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    if (!references.some((ref) => ref.value === suggestion.value)) {
      setReferences([...references, suggestion]);
      setTerm("");
      setOptions([]);
    }
  };

  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

  return (
    <Container className="mt-7" fluid>
      <Col xl="10">
        <Card className="bg-secondary shadow">
          <CardHeader className="bg-white border-0">
            <Row className="align-items-center">
              <Col xs="8">
                <h3 className="mb-0">
                  {libraryId
                    ? "Atualizar biblioteca"
                    : "Cadastro de Biblioteca"}
                </h3>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <form id="create-library" onSubmit={handleSubmit(onSubmit)}>
              <div className="pl-lg-4">
                <Row>
                  <Col md="12" lg="8">
                    <FormGroup>
                      <label
                        className="form-control-label"
                        htmlFor="client-select"
                      >
                        Cliente
                      </label>
                      <Select
                        id="client-select"
                        options={clients.sort((a, b) =>
                          a.label.localeCompare(b.label),
                        )}
                        value={selectedClient}
                        onChange={(selectedOption) =>
                          setSelectedClient(selectedOption)
                        }
                        isClearable
                        placeholder="Selecione um cliente (opcional)"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="12" lg="8">
                    <FormGroup>
                      <label className="form-control-label" htmlFor="title">
                        Título
                      </label>
                      <input
                        id="title"
                        className="form-control"
                        placeholder="Título"
                        type="text"
                        {...register("title")}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="12" lg="8">
                    <FormGroup>
                      <label
                        className="form-control-label"
                        htmlFor="description"
                      >
                        Descrição
                      </label>
                      <input
                        id="description"
                        className="form-control"
                        placeholder="Descrição"
                        type="text"
                        {...register("description")}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="12" lg="8">
                    <FormGroup>
                      <label className="form-control-label" htmlFor="term">
                        Adicionar Termo{" "}
                        <i className="fas fa-info-circle" id="libinfo" />
                        <Tooltip
                          placement="right"
                          isOpen={tooltipOpen}
                          target="libinfo"
                          toggle={toggleTooltip}
                        >
                          <div style={{ margin: "0", padding: "0" }}>
                            Para usar outra biblioteca como referência, digite @
                            e o título da biblioteca desejada.
                          </div>
                        </Tooltip>
                      </label>
                      <input
                        id="term"
                        className="form-control"
                        placeholder="Digite um termo e pressione Enter"
                        autoComplete="off"
                        type="text"
                        value={term}
                        onChange={handleInputChange}
                        onKeyPress={(e) =>
                          e.key === "Enter" && handleAddTerm(e)
                        }
                      />
                      {loadingOptions && <div>Carregando...</div>}
                      {options.length > 0 && (
                        <ul
                          className="list-group mt-2"
                          style={{
                            position: "absolute",
                            zIndex: 1000,
                            backgroundColor: "white",
                            width: "100%",
                          }}
                        >
                          {options
                            .filter(
                              (option) =>
                                !references.some(
                                  (ref) => ref.value === option.value,
                                ),
                            )
                            .map((option) => (
                              <li
                                key={option.value}
                                className="list-group-item list-group-item-action"
                                onClick={() => handleSelectSuggestion(option)}
                              >
                                {option.label}
                              </li>
                            ))}
                        </ul>
                      )}
                      {errors.term && (
                        <span className="text-danger">
                          *{errors.term.message}
                        </span>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="12" lg="8">
                    <FormGroup>
                      <div
                        id="selected-items"
                        className="form-control"
                        style={{
                          height: "auto",
                          minHeight: "50px",
                          maxHeight: "200px",
                          overflow: "auto",
                          backgroundColor: "#fff",
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "flex-start",
                          justifyContent: "flex-start",
                          padding: "5px",
                          gap: "8px",
                        }}
                      >
                        {terms.map((t, index) => (
                          <div
                            key={`term-${index}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "5px 10px",
                              background: "rgb(230, 230, 230)",
                              color: "rgb(51, 51, 51)",
                              borderRadius: "0px",
                            }}
                          >
                            {t}
                            <button
                              type="button"
                              className="select__multi-value__remove hover-remove"
                              style={{
                                marginLeft: "5px",
                                background: "rgb(230,230,230)",
                                color: "rgb(51, 51, 51)",
                                border: "none",
                                cursor: "pointer",
                              }}
                              onClick={() => handleRemoveTerm(t)}
                            >
                              <XIcon />
                            </button>
                          </div>
                        ))}
                        {references.map((ref, index) => (
                          <div
                            key={`ref-${index}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "5px 10px",
                              background: "rgb(230, 230, 230)",
                              color: "rgb(51, 51, 51)",
                              borderRadius: "0px",
                            }}
                          >
                            @{ref.label}
                            <button
                              type="button"
                              className="select__multi-value__remove hover-remove"
                              style={{
                                marginLeft: "5px",
                                background: "rgb(230,230,230)",
                                color: "rgb(51, 51, 51)",
                                border: "none",
                                cursor: "pointer",
                              }}
                              onClick={() => handleRemoveReference(ref.value)}
                            >
                              <XIcon />
                            </button>
                          </div>
                        ))}
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              </div>
              <hr className="my-4" />
              <Button type="submit">
                {loading
                  ? "Salvando..."
                  : libraryId
                    ? "Atualizar"
                    : "Adicionar"}
              </Button>
            </form>
          </CardBody>
        </Card>
      </Col>
    </Container>
  );
};

export default LibraryForm;
